import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { formatSpeakerRole } from '@src/utility/speakerRole';
import styled from 'styled-components';

import SpeakerRowActionsMenu from './SpeakerRowActionsMenu';
import SpeakerStoryRow from './SpeakerStoryRow';

import type {
  DatoSpeaker,
  SpeakerStoryRef,
} from '@src/repositories/SpeakerRepository';
import type { FC } from 'react';

type Props = {
  speaker: DatoSpeaker;
  /** The workspace's Speaker Format, for the Title column (PLAT-12261). */
  speakerFormat?: unknown;
  stories: SpeakerStoryRef[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedStoryId: string | null;
  onSelectStory: (storyId: string) => void;
  onOpenStory: (storyId: string) => void;
  showAiVoices?: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onManageAiVoices?: () => void;
};

const SpeakerRow: FC<Props> = ({
  speaker,
  speakerFormat,
  stories,
  isExpanded,
  onToggleExpand,
  selectedStoryId,
  onSelectStory,
  onOpenStory,
  showAiVoices = false,
  onEdit,
  onRemove,
  onManageAiVoices,
}) => {
  const hasStories = stories.length > 0;
  const aiVoiceCount = speaker.aiVoices?.length ?? 0;
  return (
    <Container data-testid="speaker-row">
      <Row onClick={onEdit}>
        <Left>
          <ExpandArrow
            type="button"
            disabled={!hasStories}
            $visible={hasStories}
            $expanded={isExpanded}
            onClick={event => {
              event.stopPropagation();
              onToggleExpand();
            }}
            aria-label={
              hasStories
                ? isExpanded
                  ? 'Collapse stories'
                  : 'Expand stories'
                : undefined
            }
          >
            {hasStories && <ChevronDownIcon width="12" height="12" />}
          </ExpandArrow>
          <SpeakerAvatar
            name={speaker.name}
            color="var(--surface-elevated)"
            headshotUrl={speaker.headshots?.[0]?.url}
            size={36}
            initialsLength={2}
            initialsColor="var(--text-secondary)"
            initialsFontSize="var(--text-xs)"
            initialsFontWeight="var(--font-weight-medium)"
          />
          <Name>{speaker.name}</Name>
        </Left>
        <Right>
          <TitleCol
            title={formatSpeakerRole(speaker, speakerFormat) || undefined}
          >
            {formatSpeakerRole(speaker, speakerFormat) || <Muted>—</Muted>}
          </TitleCol>
          <CreatedByCol title={speaker.createdByUser?.name || undefined}>
            {speaker.createdByUser?.name || <Muted>—</Muted>}
          </CreatedByCol>
          {showAiVoices && (
            <NumericCol>
              {aiVoiceCount > 0 ? aiVoiceCount : <Muted>0</Muted>}
            </NumericCol>
          )}
          <StoriesCol>
            {hasStories ? stories.length : <Muted>0</Muted>}
          </StoriesCol>
          <SpeakerRowActionsMenu
            onEdit={onEdit}
            onRemove={onRemove}
            onManageAiVoices={onManageAiVoices}
          />
        </Right>
      </Row>
      {isExpanded && hasStories && (
        <StoryList>
          {stories.map(story => (
            <SpeakerStoryRow
              key={story.id}
              story={story}
              isSelected={selectedStoryId === story.id}
              onSelect={onSelectStory}
              onOpen={onOpenStory}
            />
          ))}
        </StoryList>
      )}
    </Container>
  );
};

export default SpeakerRow;

const Container = styled.div`
  border-bottom: 1px solid
    color-mix(in srgb, var(--text-muted) 40%, transparent);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px var(--spacing-md);
  cursor: pointer;
  transition: background 120ms ease;

  &:hover {
    background: var(--surface);
  }
`;

const ExpandArrow = styled.button<{ $visible: boolean; $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* Generous hit area — the icon stays 12px but the whole width + full row
     height is clickable so a near-miss toggles expand instead of falling
     through to the row's edit handler. */
  width: 28px;
  align-self: stretch;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: ${({ $visible }) => ($visible ? 'pointer' : 'default')};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};

  & > svg {
    transition: transform 150ms ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 0 0 280px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: var(--text-sm);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex: 1;
  min-width: 0;
`;

const TitleCol = styled.span`
  flex: 1;
  min-width: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CreatedByCol = styled.span`
  width: 140px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StoriesCol = styled.span`
  width: 90px;
  text-align: right;
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const NumericCol = styled.span`
  width: 90px;
  text-align: right;
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const Muted = styled.span`
  color: var(--text-muted);
`;

const StoryList = styled.div`
  display: flex;
  flex-direction: column;
  /* ~5 story cards tall before scrolling. */
  max-height: 270px;
  overflow-y: auto;
  background: var(--surface);
`;
